import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Col,
  Popconfirm,
  Row,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { AppBarCMS } from '../../../../basic';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { CollapseBlock, MainTitle, Spinner } from '../../../../common';
import TableFilter from '../../../../common/TableFilter/TableFilter.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import checkPermission from '../../../../../utils/helpers/checkPermission.jsx';
import {
  useDeletePhdDissertationExamApplicationMutation,
  useDeletePhdDissertationDefenseApplicationMutation,
  useDeletePhdSubjectsExamApplicationMutation,
  useGetPhdDissertationDefenseApplicationsQuery,
  useGetPhdDissertationExamApplicationsQuery,
  useGetPhdSubjectsExamApplicationsQuery,
  useLazyGetPhdDissertationDefenseApplicationDocumentQuery,
  useLazyGetPhdDissertationExamApplicationDocumentQuery,
  useLazyGetPhdSubjectsExamApplicationDocumentQuery,
  useSubmitPhdDissertationDefenseApplicationMutation,
  useSubmitPhdDissertationExamApplicationMutation,
  useSubmitPhdSubjectsExamApplicationMutation,
} from '../../../../../redux/services/phd';
import usePhdStudentProfile from '../usePhdStudentProfile.jsx';
import { buildPhdApplicationTabs } from './phdApplicationTabs.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import {
  isApplicationSubmitted,
  PHD_APPLICATION_CHANGE_CONTACT_MESSAGE,
  renderApplicationSubmissionTag,
} from '../utils/applicationSubmissionState.jsx';
import PhdArchiveFilter, { usePhdArchiveStatus } from '../PhdArchiveFilter.jsx';
import { getPhdApiErrorMessage } from '../utils/phdApiFeedback.js';

const FILTER_STORAGE_KEY = 'phd-applications-filter';

const PhdApplicationsPage = ({ initialTab }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const canLoadStudentProfile = Boolean(
    user?.permissions?.['phd-student-plan.index'],
  );
  const { data: studentProfile } = usePhdStudentProfile({
    enabled: canLoadStudentProfile,
  });
  const isStudentContext = Boolean(studentProfile?.id);
  const { deleteSuccess, deleteFailed, saveFailed, successMessage } =
    useToastMessages();
  const { archiveStatus, setArchiveStatus } = usePhdArchiveStatus(
    'phd-applications-archive-status',
  );
  const effectiveArchiveStatus = isAdmin ? archiveStatus : 'active';
  const canViewDissertationExam = checkPermission(
    user,
    'phd-dissertation-exam-applications.index',
    isAdmin,
  );
  const canViewDissertationDefense = checkPermission(
    user,
    'phd-dissertation-defense-applications.index',
    isAdmin,
  );
  const canViewSubjectsExam = checkPermission(
    user,
    'phd-subjects-exam-applications.index',
    isAdmin,
  );
  const storedTab =
    typeof window !== 'undefined'
      ? window.localStorage.getItem(FILTER_STORAGE_KEY)
      : null;
  const [activeTab, setActiveTab] = useState(() => {
    const candidate = initialTab || storedTab;

    if (
      candidate === 'dissertationExam' &&
      canViewDissertationExam
    ) {
      return candidate;
    }

    if (
      candidate === 'dissertationDefense' &&
      canViewDissertationDefense
    ) {
      return candidate;
    }

    if (candidate === 'subjectsExam' && canViewSubjectsExam) {
      return candidate;
    }

    if (canViewDissertationExam) {
      return 'dissertationExam';
    }

    if (canViewDissertationDefense) {
      return 'dissertationDefense';
    }

    return canViewSubjectsExam ? 'subjectsExam' : null;
  });
  const [pagination, setPagination] = useState({ page: 1, per_page: 25 });
  const queryParams = {
    archive_status: effectiveArchiveStatus,
    paginated: 1,
    page: pagination.page,
    per_page: pagination.per_page,
  };

  useEffect(() => {
    if (
      isStudentContext &&
      !isAdmin &&
      location.pathname.startsWith(routes.cmsPhdApplications)
    ) {
      navigate(routes.cmsPhdMyApplications, { replace: true });
    }
  }, [isAdmin, isStudentContext, location.pathname, navigate]);

  const {
    data: dissertationExamApplicationsResponse,
    isLoading: isLoadingDissertationExam,
    error: dissertationExamError,
    refetch: refetchDissertationExam,
  } = useGetPhdDissertationExamApplicationsQuery(
    queryParams,
    {
      skip: !canViewDissertationExam || (!isStudentContext && activeTab !== 'dissertationExam'),
    },
  );
  const {
    data: dissertationDefenseApplicationsResponse,
    isLoading: isLoadingDissertationDefense,
    error: dissertationDefenseError,
    refetch: refetchDissertationDefense,
  } = useGetPhdDissertationDefenseApplicationsQuery(
    queryParams,
    {
      skip:
        !canViewDissertationDefense || (!isStudentContext && activeTab !== 'dissertationDefense'),
    },
  );
  const {
    data: subjectsExamApplicationsResponse,
    isLoading: isLoadingSubjectsExam,
    error: subjectsExamError,
    refetch: refetchSubjectsExam,
  } = useGetPhdSubjectsExamApplicationsQuery(
    queryParams,
    {
      skip: !canViewSubjectsExam || (!isStudentContext && activeTab !== 'subjectsExam'),
    },
  );

  const dissertationExamApplications =
    dissertationExamApplicationsResponse?.data;
  const dissertationDefenseApplications =
    dissertationDefenseApplicationsResponse?.data;
  const subjectsExamApplications = subjectsExamApplicationsResponse?.data;

  const [deleteDissertationExamApplication, { isLoading: isDeletingExam }] =
    useDeletePhdDissertationExamApplicationMutation();
  const [
    deleteDissertationDefenseApplication,
    { isLoading: isDeletingDefense },
  ] = useDeletePhdDissertationDefenseApplicationMutation();
  const [deleteSubjectsExamApplication, { isLoading: isDeletingSubjects }] =
    useDeletePhdSubjectsExamApplicationMutation();
  const [submitDissertationExamApplication, { isLoading: isSubmittingExam }] =
    useSubmitPhdDissertationExamApplicationMutation();
  const [
    submitDissertationDefenseApplication,
    { isLoading: isSubmittingDefense },
  ] = useSubmitPhdDissertationDefenseApplicationMutation();
  const [submitSubjectsExamApplication, { isLoading: isSubmittingSubjects }] =
    useSubmitPhdSubjectsExamApplicationMutation();
  const [downloadDissertationExamDoc, { isLoading: isDownloadingExam }] =
    useLazyGetPhdDissertationExamApplicationDocumentQuery();
  const [downloadDissertationDefenseDoc, { isLoading: isDownloadingDefense }] =
    useLazyGetPhdDissertationDefenseApplicationDocumentQuery();
  const [downloadSubjectsExamDoc, { isLoading: isDownloadingSubjects }] =
    useLazyGetPhdSubjectsExamApplicationDocumentQuery();

  const dependencyStudent = useMemo(() => {
    if (!studentProfile) {
      return null;
    }

    return {
      ...studentProfile,
      dissertationExamApplications:
        dissertationExamApplications ??
        studentProfile.dissertationExamApplications,
      dissertationDefenseApplications:
        dissertationDefenseApplications ??
        studentProfile.dissertationDefenseApplications,
      subjectsExamApplications:
        subjectsExamApplications ?? studentProfile.subjectsExamApplications,
    };
  }, [
    dissertationDefenseApplications,
    dissertationExamApplications,
    studentProfile,
    subjectsExamApplications,
  ]);

  const tabDefinitions = useMemo(
    () =>
      buildPhdApplicationTabs({
        t,
        dependencyStudent,
        isStudentContext,
        studentProfileId: studentProfile?.id,
        canViewDissertationExam,
        canViewDissertationDefense,
        canViewSubjectsExam,
        dissertationExamApplications,
        dissertationDefenseApplications,
        subjectsExamApplications,
        dissertationExamPagination: dissertationExamApplicationsResponse,
        dissertationDefensePagination: dissertationDefenseApplicationsResponse,
        subjectsExamPagination: subjectsExamApplicationsResponse,
        isLoadingDissertationExam,
        isLoadingDissertationDefense,
        isLoadingSubjectsExam,
        refetchDissertationExam,
        refetchDissertationDefense,
        refetchSubjectsExam,
        deleteDissertationExamApplication,
        deleteDissertationDefenseApplication,
        deleteSubjectsExamApplication,
      }),
    [
      canViewDissertationDefense,
      canViewDissertationExam,
      canViewSubjectsExam,
      deleteDissertationDefenseApplication,
      deleteDissertationExamApplication,
      deleteSubjectsExamApplication,
      dependencyStudent,
      dissertationDefenseApplications,
      dissertationDefenseApplicationsResponse,
      dissertationExamApplications,
      dissertationExamApplicationsResponse,
      isLoadingDissertationDefense,
      isLoadingDissertationExam,
      isLoadingSubjectsExam,
      refetchDissertationDefense,
      refetchDissertationExam,
      refetchSubjectsExam,
      subjectsExamApplications,
      subjectsExamApplicationsResponse,
      isStudentContext,
      studentProfile?.id,
      t,
    ],
  );

  const visibleTabs = useMemo(
    () => tabDefinitions.filter((tab) => tab.canView),
    [tabDefinitions],
  );

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(visibleTabs[0]?.value || null);
    }
  }, [activeTab, visibleTabs]);

  const activeConfig =
    visibleTabs.find((tab) => tab.value === activeTab) || visibleTabs[0];
  const pageTitle = initialTab
    ? t('Moje prihlášky')
    : t('Prihlášky doktorandov');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const handleDelete = async (row, tabConfig = activeConfig) => {
    if (!tabConfig?.deleteMutation) return;
    const id = row?.id;
    if (!id) {
      return;
    }

    try {
      await tabConfig.deleteMutation(id).unwrap();
      deleteSuccess();
      tabConfig.refetch?.();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const handleTabChange = (value) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(FILTER_STORAGE_KEY, value);
    }
    setPagination((current) => ({ ...current, page: 1 }));
    setActiveTab(value);
  };

  const handleArchiveStatusChange = (value) => {
    setPagination((current) => ({ ...current, page: 1 }));
    setArchiveStatus(value);
  };

  const handlePaginationChange = (nextPagination) => {
    setPagination({
      page: nextPagination.current,
      per_page: nextPagination.pageSize,
    });
  };

  if (!visibleTabs.length) {
    return (
      <>
        <AppBarCMS
          title={pageTitle}
          backUrl={routes.cmsDashboard}
          backUrlTooltip={t('Nástenka')}
        />
        <div style={{ margin: '10px' }}>
          <PhdPageContext
            title={t('Ako fungujú prihlášky doktoranda?')}
            description={t(
              'Na tejto stránke sa podľa vašej roly zobrazujú dostupné typy prihlášok. Každá nadväzuje na splnené údaje štúdia a po odovzdaní pokračuje príslušným skúšobným alebo obhajobným krokom.',
            )}
          />
          <CollapseBlock>
            <MainTitle title={pageTitle} />
            <p>{t('Nemáte oprávnenie na zobrazenie prihlášok.')}</p>
          </CollapseBlock>
        </div>
      </>
    );
  }

  const TableComponent = activeConfig?.TableComponent;
  const tableProps = {
    onDetailClick: (row) => navigate(`${activeConfig.editUrl}/${row.id}`),
    onDelete: handleDelete,
    onDownload: isAdmin
      ? (row) => handleDownloadByType(activeConfig, row)
      : undefined,
    isDownloading:
      activeConfig?.value === 'dissertationExam'
        ? isDownloadingExam
        : activeConfig?.value === 'dissertationDefense'
          ? isDownloadingDefense
          : isDownloadingSubjects,
  };
  if (activeConfig?.dataPropName) {
    tableProps[activeConfig.dataPropName] = activeConfig.data || [];
  }
  tableProps.pagination = {
    current: activeConfig?.pagination?.current_page ?? pagination.page,
    pageSize: activeConfig?.pagination?.per_page ?? pagination.per_page,
    total: activeConfig?.pagination?.total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50, 100],
  };
  tableProps.onPaginationChange = handlePaginationChange;

  const isStudentOverviewLoading = visibleTabs.some((tab) => tab.isLoading);
  const isStudentOverview = isStudentContext && !isAdmin;
  const isAdminOverview = isAdmin && !isStudentOverview;
  const downloadLoadingByType = {
    dissertationExam: isDownloadingExam,
    dissertationDefense: isDownloadingDefense,
    subjectsExam: isDownloadingSubjects,
  };
  const deleteLoadingByType = {
    dissertationExam: isDeletingExam,
    dissertationDefense: isDeletingDefense,
    subjectsExam: isDeletingSubjects,
  };
  const submitLoadingByType = {
    dissertationExam: isSubmittingExam,
    dissertationDefense: isSubmittingDefense,
    subjectsExam: isSubmittingSubjects,
  };

  const renderTabTable = (tab) => {
    const TabTableComponent = tab?.TableComponent;
    if (!TabTableComponent) {
      return null;
    }

    const tabProps = {
      onDetailClick: (row) => navigate(`${tab.editUrl}/${row.id}`),
      onDelete: (row) => handleDelete(row, tab),
      canDeleteSubmitted: isAdmin,
      onDownload: isAdmin ? (row) => handleDownloadByType(tab, row) : undefined,
      isDownloading: downloadLoadingByType[tab.value],
    };

    if (tab?.dataPropName) {
      tabProps[tab.dataPropName] = tab.data || [];
    }
    tabProps.pagination = {
      current: tab.pagination?.current_page ?? pagination.page,
      pageSize: tab.pagination?.per_page ?? pagination.per_page,
      total: tab.pagination?.total ?? 0,
      showSizeChanger: true,
      pageSizeOptions: [10, 25, 50, 100],
    };
    tabProps.onPaginationChange = handlePaginationChange;

    if (tab?.isLoading) {
      return <Spinner />;
    }

    return <TabTableComponent {...tabProps} />;
  };

  async function handleDownloadByType(tab, application) {
    if (!application?.id) {
      return;
    }

    try {
      let response;
      if (tab.value === 'dissertationExam') {
        response = await downloadDissertationExamDoc(application.id).unwrap();
      } else if (tab.value === 'dissertationDefense') {
        response = await downloadDissertationDefenseDoc(
          application.id,
        ).unwrap();
      } else if (tab.value === 'subjectsExam') {
        response = await downloadSubjectsExamDoc(application.id).unwrap();
      }

      if (!response) {
        return;
      }

      const fallbackName = `${application.student_display_name || t('Prihláška')}.docx`;
      downloadBlobResponse(response, fallbackName);
    } catch (error) {
      saveFailed(error);
    }
  }

  async function handleSubmitByType(tab, application) {
    if (!application?.id) {
      return;
    }

    try {
      if (tab.value === 'dissertationExam') {
        await submitDissertationExamApplication(application.id).unwrap();
      } else if (tab.value === 'dissertationDefense') {
        await submitDissertationDefenseApplication(application.id).unwrap();
      } else if (tab.value === 'subjectsExam') {
        await submitSubjectsExamApplication(application.id).unwrap();
      }

      successMessage({ message: t('Prihláška bola odovzdaná') });
      tab.refetch?.();
    } catch (error) {
      saveFailed(error);
    }
  }

  if (isStudentOverview) {
    return (
      <>
        <AppBarCMS
          title={pageTitle}
          backUrl={routes.cmsDashboard}
          backUrlTooltip={t('Nástenka')}
        />
        <div style={{ margin: '10px' }}>
          <PhdPageContext
            title={t('Ako fungujú prihlášky doktoranda?')}
            description={t(
              'Tu vytvoríte alebo otvoríte vlastné prihlášky. Rozpracovanú prihlášku možno upraviť; odovzdaná verzia ostáva iba na zobrazenie a export.',
            )}
          />
          <CollapseBlock>
            <MainTitle title={t('Moje prihlášky')} />
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(PHD_APPLICATION_CHANGE_CONTACT_MESSAGE)}
            />
            {isStudentOverviewLoading ? (
              <Spinner />
            ) : (
              <Row gutter={[16, 16]}>
                {visibleTabs.map((tab) => {
                  const hasApplication = Boolean(
                    tab.existingStudentApplication?.id,
                  );
                  const buttonLabel = hasApplication
                    ? isApplicationSubmitted(tab.existingStudentApplication)
                      ? t('Zobraziť prihlášku')
                      : t('Upraviť prihlášku')
                    : t('Vytvoriť prihlášku');
                  const isSubmitted = isApplicationSubmitted(
                    tab.existingStudentApplication,
                  );
                  const createMissingItems =
                    tab.createDependencyState?.missingItems || [];
                  const isCreateDisabled =
                    !hasApplication && createMissingItems.length > 0;
                  const createDisabledMessage = isCreateDisabled
                    ? t(
                        'Prihlášku nie je možné vytvoriť, pretože neexistuje: {{items}}.',
                        { items: createMissingItems.join(', ') },
                      )
                    : undefined;

                  const buttonAction = () => {
                    if (isCreateDisabled) {
                      return;
                    }

                    if (hasApplication) {
                      navigate(
                        `${tab.editUrl}/${tab.existingStudentApplication.id}`,
                      );
                      return;
                    }

                    if (tab.createUrl) {
                      navigate(tab.createUrl);
                    }
                  };

                  return (
                    <Col xs={24} md={24} lg={24} key={tab.value}>
                      <Card title={tab.studentLabel || tab.label}>
                        <p style={{ marginBottom: 12 }}>
                          <Tag color={hasApplication ? 'green' : 'default'}>
                            {hasApplication
                              ? t('Prihláška vytvorená')
                              : t('Prihláška nevytvorená')}
                          </Tag>
                          {hasApplication ? (
                            <span style={{ marginLeft: 8 }}>
                              {renderApplicationSubmissionTag(
                                tab.existingStudentApplication,
                                t,
                              )}
                            </span>
                          ) : null}
                        </p>
                        {hasApplication && !isSubmitted ? (
                          <p style={{ marginBottom: 12 }}>
                            {t(
                              'Rozpracovanú prihlášku môžete upravovať a vymazať. Oficiálnou sa stane až po potvrdení a odovzdaní.',
                            )}
                          </p>
                        ) : null}
                        <Space size={8}>
                          <Tooltip title={createDisabledMessage}>
                            <span>
                              <Button
                                type="primary"
                                onClick={buttonAction}
                                disabled={isCreateDisabled}
                              >
                                {buttonLabel}
                              </Button>
                            </span>
                          </Tooltip>
                          {hasApplication && (
                            <>
                              {!isSubmitted ? (
                                <Popconfirm
                                  title={t(
                                    'Naozaj chcete potvrdiť a odovzdať prihlášku?',
                                  )}
                                  description={t(
                                    'Po odovzdaní už nebude možné prihlášku upravovať ani vymazať.',
                                  )}
                                  okText={t('Odovzdať')}
                                  cancelText={t('Zrušiť')}
                                  onConfirm={() =>
                                    handleSubmitByType(
                                      tab,
                                      tab.existingStudentApplication,
                                    )
                                  }
                                >
                                  <Button
                                    icon={<CheckOutlined />}
                                    loading={submitLoadingByType[tab.value]}
                                  >
                                    {t('Potvrdiť a odovzdať')}
                                  </Button>
                                </Popconfirm>
                              ) : null}
                              {isSubmitted ? (
                                <Tooltip title={t('Stiahnuť DOCX')}>
                                  <Button
                                    aria-label={t('Stiahnuť DOCX')}
                                    icon={<DownloadOutlined />}
                                    onClick={() =>
                                      handleDownloadByType(
                                        tab,
                                        tab.existingStudentApplication,
                                      )
                                    }
                                    loading={downloadLoadingByType[tab.value]}
                                  />
                                </Tooltip>
                              ) : null}
                              {!isSubmitted ? (
                                <Popconfirm
                                  title={t('Naozaj chcete vymazať prihlášku?')}
                                  okText={t('Vymazať')}
                                  cancelText={t('Zrušiť')}
                                  onConfirm={() =>
                                    handleDelete(
                                      tab.existingStudentApplication,
                                      tab,
                                    )
                                  }
                                >
                                  <Tooltip title={t('Vymazať prihlášku')}>
                                    <Button
                                      aria-label={t('Vymazať prihlášku')}
                                      icon={<DeleteOutlined />}
                                      danger
                                      loading={deleteLoadingByType[tab.value]}
                                    />
                                  </Tooltip>
                                </Popconfirm>
                              ) : null}
                            </>
                          )}
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </CollapseBlock>
        </div>
      </>
    );
  }

  if (isAdminOverview) {
    return (
      <>
        <AppBarCMS
          title={pageTitle}
          backUrl={routes.cmsDashboard}
          backUrlTooltip={t('Nástenka')}
          createText={activeConfig?.createText}
          createUrl={activeConfig?.createUrl}
          permissionsBasic={activeConfig?.permissionsBasic}
        />
        <div style={{ margin: '10px' }}>
          <PhdPageContext
            title={t('Ako fungujú prihlášky doktoranda?')}
            description={t(
              'Administrátor doktorandského štúdia tu kontroluje všetky typy prihlášok. Po odovzdaní prihlášky na dizertačnú skúšku alebo obhajobu pokračujte návrhom príslušnej komisie.',
            )}
          />
          <PhdArchiveFilter
            archiveStatus={archiveStatus}
            onChange={handleArchiveStatusChange}
            storageKey="phd-applications-archive-status"
          />
          <CollapseBlock>
            <TableFilter
              title={t('Filter prihlášok')}
              storageKey={`${FILTER_STORAGE_KEY}-admin`}
              value={activeConfig?.value}
              setValue={handleTabChange}
              options={visibleTabs.map((tab) => ({
                value: tab.value,
                label: tab.label,
              }))}
            />
          </CollapseBlock>
          <CollapseBlock>
            <MainTitle title={activeConfig?.label || pageTitle} />
            {activeConfig ? renderTabTable(activeConfig) : null}
          </CollapseBlock>
        </div>
      </>
    );
  }

  const loadError =
    dissertationExamError || dissertationDefenseError || subjectsExamError;

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title={getPhdApiErrorMessage(
          loadError,
          t(
            'PhD údaje sa nepodarilo načítať. Obnovte stránku alebo skúste znova.',
          ),
        )}
      />
    );
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={
          activeConfig?.existingStudentApplication
            ? undefined
            : activeConfig?.createText
        }
        createUrl={
          activeConfig?.existingStudentApplication
            ? undefined
            : activeConfig?.createUrl
        }
        permissionsBasic={activeConfig?.permissionsBasic}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú prihlášky doktoranda?')}
          description={t(
            'Vyberte typ prihlášky, skontrolujte jej študijné podklady a pokračujte vytvorením alebo otvorením existujúceho záznamu.',
          )}
        />
        <CollapseBlock>
          <TableFilter
            title={t('Filter prihlášok')}
            storageKey={FILTER_STORAGE_KEY}
            value={activeConfig?.value}
            setValue={handleTabChange}
            options={visibleTabs.map((tab) => ({
              value: tab.value,
              label: tab.label,
            }))}
          />
        </CollapseBlock>

        <CollapseBlock>
          <MainTitle title={activeConfig?.label || pageTitle} />
          {activeConfig?.isLoading ? (
            <Spinner />
          ) : (
            TableComponent && <TableComponent {...tableProps} />
          )}
        </CollapseBlock>
      </div>
    </>
  );
};

PhdApplicationsPage.propTypes = {
  initialTab: PropTypes.string,
};

export default PhdApplicationsPage;
